import { format, formatDistanceToNowStrict, isValid, parseISO } from 'date-fns'
import { useCompanySettings } from '@/hooks/useCompanySettings'

export interface DateValueProps {
  value: string | Date | null | undefined
  /** Show "2 hours ago" instead of an absolute date — used by the activity feed. */
  relative?: boolean
  /** Include the time alongside the date. */
  withTime?: boolean
  className?: string
}

/** Renders a date in the company's configured format, never a hardcoded one. */
export function DateValue({ value, relative, withTime, className }: DateValueProps) {
  const { settings } = useCompanySettings()

  if (!value) return <span className={className}>—</span>

  const date = typeof value === 'string' ? parseISO(value) : value
  if (!isValid(date)) return <span className={className}>—</span>

  // ISO when the company's format could not be loaded: unambiguous everywhere, and visibly
  // not the format anybody configured, rather than a guess that looks configured.
  const dateFormat = settings?.dateFormat ?? 'yyyy-MM-dd'
  const timeFormat = settings?.timeFormat ?? 'HH:mm'
  const absolute = format(date, withTime ? `${dateFormat} ${timeFormat}` : dateFormat)

  if (relative) {
    // The absolute value stays available on hover, so "2 months ago" is never a dead end.
    return (
      <span className={className} title={absolute}>
        {formatDistanceToNowStrict(date, { addSuffix: true })}
      </span>
    )
  }

  return (
    <time dateTime={date.toISOString()} className={className}>
      {absolute}
    </time>
  )
}
